/**
 * LegalTemplates PDF Editor — unsaved-work safety net (IndexedDB drafts)
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { redrawAllPages, updateUndoRedoButtons } from './annotations.js';
import {
    DRAFT_DB_NAME,
    DRAFT_STORE_NAME,
    DRAFT_VERSION,
    draftBannerEl,
    draftBannerMetaEl,
    draftStore,
    session,
    state,
} from './context.js';
import { hasPendingEdits } from './core.js';
import {
    collectKnownFormFieldNames,
    hasPendingFormFieldChanges,
    updateFormFillSummary,
} from './form-fill.js';
import { setWatermarkFeedback } from './modals.js';
import { commitAllInlineEditors } from './text.js';
import { setStatus, showAlert } from './ui.js';
import { renderAllPages } from './viewport.js';

export function hasUnsavedWork() {
    return !!(hasPendingEdits() || hasPendingFormFieldChanges());
}

export function openDraftDb() {
    return new Promise((resolve) => {
        try {
            if (!window.indexedDB) {
                resolve(null);
                return;
            }
            const request = window.indexedDB.open(DRAFT_DB_NAME, 1);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(DRAFT_STORE_NAME)) {
                    db.createObjectStore(DRAFT_STORE_NAME);
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
            request.onblocked = () => resolve(null);
        } catch (error) {
            resolve(null);
        }
    });
}

export async function draftDbRequest(mode, run) {
    const db = await openDraftDb();
    if (!db) {
        return null;
    }
    return new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
            if (!settled) {
                settled = true;
                resolve(value);
            }
        };
        try {
            const transaction = db.transaction(DRAFT_STORE_NAME, mode);
            const store = transaction.objectStore(DRAFT_STORE_NAME);
            const request = run(store);
            request.onsuccess = () => finish(request.result === undefined ? null : request.result);
            request.onerror = () => finish(null);
            transaction.oncomplete = () => db.close();
            transaction.onerror = () => {
                finish(null);
                db.close();
            };
            transaction.onabort = () => {
                finish(null);
                db.close();
            };
        } catch (error) {
            finish(null);
            try {
                db.close();
            } catch (closeError) {
                // Ignore.
            }
        }
    });
}

export async function computeDocumentKey(bytes) {
    if (!bytes || !bytes.length) {
        return null;
    }
    try {
        if (window.crypto && window.crypto.subtle) {
            const digest = await window.crypto.subtle.digest('SHA-256', bytes.slice());
            return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
        }
    } catch (error) {
        // Fall through to the sampled hash.
    }
    let hash = 2166136261;
    const step = Math.max(1, Math.floor(bytes.length / 4096));
    for (let index = 0; index < bytes.length; index += step) {
        hash ^= bytes[index];
        hash = Math.imul(hash, 16777619) >>> 0;
    }
    return `${bytes.length}:${hash.toString(16)}`;
}

export function scheduleDraftSave() {
    if (!state.localDraft) {
        return;
    }
    window.clearTimeout(state.localDraft.timer);
    state.localDraft.timer = window.setTimeout(() => {
        persistDraft().catch(() => {});
    }, 900);
}

export async function persistDraft() {
    const key = state.localDraft.key;
    if (!key) {
        return;
    }
    const annotations = Array.isArray(state.annotations) ? state.annotations : [];
    const overrides = Array.from(state.formFieldOverrides.entries());
    if (!annotations.length && !overrides.length) {
        await draftStore.delete(key);
        return;
    }
    let snapshot;
    try {
        snapshot = JSON.parse(JSON.stringify(annotations));
    } catch (error) {
        return;
    }
    await draftStore.put(key, {
        version: DRAFT_VERSION,
        savedAt: Date.now(),
        docName: String(session.docName || ''),
        renderWidth: Number(state.baseRenderWidth) || 0,
        lastActivePage: Number(state.lastActivePage) || 1,
        annotations: snapshot,
        formFieldOverrides: overrides,
    });
}

export async function clearDraft() {
    window.clearTimeout(state.localDraft.timer);
    state.localDraft.restorable = null;
    hideDraftBanner();
    const key = state.localDraft.key;
    if (key) {
        await draftStore.delete(key);
    }
}

export function hideDraftBanner() {
    if (draftBannerEl) {
        draftBannerEl.classList.add('hidden');
    }
}

export function describeDraftAge(timestamp) {
    const savedAt = Number(timestamp) || 0;
    if (!savedAt) {
        return 'Unsaved edits';
    }
    const minutes = Math.max(0, Math.round((Date.now() - savedAt) / 60000));
    if (minutes < 1) {
        return 'Edited moments ago';
    }
    if (minutes < 60) {
        return `Edited ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
        return `Edited ${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    const days = Math.round(hours / 24);
    return `Edited ${days} day${days === 1 ? '' : 's'} ago`;
}

export async function offerDraftRestore(key) {
    if (!draftBannerEl || !key) {
        return;
    }
    const draft = await draftStore.get(key);
    if (!draft || key !== state.localDraft.key) {
        return;
    }
    const hasContent = !!(
        (Array.isArray(draft.annotations) && draft.annotations.length) ||
        (Array.isArray(draft.formFieldOverrides) && draft.formFieldOverrides.length)
    );
    if (!hasContent) {
        return;
    }
    if (state.annotations.length || state.formFieldOverrides.size) {
        // The session already carries edits; never overwrite them silently.
        return;
    }
    state.localDraft.restorable = draft;
    if (draftBannerMetaEl) {
        draftBannerMetaEl.textContent = `${describeDraftAge(draft.savedAt)} in this browser. They are not in the file yet.`;
    }
    draftBannerEl.classList.remove('hidden');
}

export function applyDraftToState(draft) {
    let annotations = [];
    try {
        annotations = Array.isArray(draft.annotations) ? JSON.parse(JSON.stringify(draft.annotations)) : [];
    } catch (error) {
        annotations = [];
    }
    state.annotations = annotations;
    state.formFieldOverrides.clear();
    const knownFields = collectKnownFormFieldNames();
    (Array.isArray(draft.formFieldOverrides) ? draft.formFieldOverrides : []).forEach((entry) => {
        if (!Array.isArray(entry) || entry.length < 2) {
            return;
        }
        const [fieldName, value] = entry;
        if (!knownFields.size || knownFields.has(fieldName)) {
            state.formFieldOverrides.set(fieldName, value);
        }
    });
    state.history = [];
    state.redo = [];
    state.selectedAnnotationId = null;
    state.annotationDirty = state.annotations.length > 0;
    updateUndoRedoButtons();
    redrawAllPages();
    updateFormFillSummary();
    hideDraftBanner();
    state.localDraft.restorable = null;
    scheduleDraftSave();
    setStatus('Unsaved edits restored');
    showAlert('Your unsaved edits are back. Use Save Edits to write them into the file.');
}

export async function restoreDraft() {
    const draft = state.localDraft.restorable;
    if (!draft) {
        hideDraftBanner();
        return;
    }
    hideDraftBanner();
    commitAllInlineEditors(false);
    const renderWidth = Number(draft.renderWidth) || 0;
    if (renderWidth > 0 && Math.abs(renderWidth - (Number(state.baseRenderWidth) || 0)) > 0.5) {
        // The draft's coordinates belong to the width it was captured at.
        // Re-render at that width; the zoom fits it on screen.
        state.localDraft.pendingRestore = draft;
        await renderAllPages({ targetWidth: renderWidth });
        return;
    }
    applyDraftToState(draft);
}

export function resetWatermarkDraft() {
    state.watermark.enabled = false;
    state.watermark.pendingRemoval = false;
    state.watermark.text = state.watermark.text || 'CONFIDENTIAL';
    setWatermarkFeedback('Click Apply to stage a watermark, then Save Edits to apply it permanently.');
    redrawAllPages();
}
